import { ChangeEvent, useEffect } from "react";

import { CaseAdmin, Clinic, Lab } from "pwapi";

import { selectFeatureFlag } from "shared/reducers/sysReducer";
import {
  billingTypeEnumToLabel,
  displayPriority,
} from "shared/utils/caseUtils";
import { locationOptions } from "shared/utils/usRegions";

import CustomInput from "components/customInputs/CustomInput";
import DateInput from "components/customInputs/DateInput";
import MultiSelectColumnInput from "components/customInputs/MultiColumnSelectInput";
import SelectInput from "components/customInputs/SelectInput";
import TextInput from "components/customInputs/TextInput";

import CliaCertificateFilter from "./CliaCertificateFilter";
import { AdminClinicFilter } from "./ClinicFilter";
import ClinicGroupFilter from "./ClinicGroupFilter";
import SonicPracticeFilter from "./SonicPracticeFilter";
import { useAppSelector } from "../../../store/hooks";
import {
  mapQueryFilterToLabel,
  mapQueryFilterToOptions,
} from "../casesTable/CaseAdminFilters";
import {
  CaseFilterState,
  FilterLabels,
  FilterOptions,
} from "../casesTable/CaseAdminFilters.contracts";

type CaseFiltersProps = CaseFilterState & {
  patientClinic?: Clinic | null;
  labs: Lab[];
  isManuallyAccessioned?: boolean;

  onChange: Function;
  onChangeFilterObject: Function;
  onInitializeClinic: Function;
  onChangeManuallyAccessioned: Function;
};

export const qaReviewLabels = {
  reviewed: "Reviewed",
  unreviewed: "Unreviewed",
  "on-hold": "On Hold",
};

export const mapCaseFilterToLabel: FilterLabels<CaseFilterState> = {
  accession: "Accession",
  accessioningLab: "Accessioning Lab",
  activeAssignmentName: "Active Assignment",
  billingComponent: "Billing Component",
  billerName: "Biller Name",
  billingType: "Billing Type",
  clinicNumber: "Clinic Number",
  grossedByCert: "TC CLIA Certificate",
  insuranceCategory: "Insurance Category",
  orderingDoctorName: "Ordering Provider",
  patientAddressState: "Patient State",
  patientClinicClinicGroupName: "Clinic Group",
  patientClinicLocationState: "Clinic State",
  patientClinicId: "Clinic",
  // patientClinicName: "Clinic",
  patientClinicSonicPracticeName: "Sonic Practice", // deprecated in favor of `sonicPracticeName`
  sonicPracticeName: "Sonic Practice",
  patientDateOfBirth: "Patient Date of Birth",
  patientInsuranceInsuranceCompanyName: "Patient Insurance Name",
  patientName: "Patient",
  pcClia: "PC CLIA Certificate",
  pcCliaSource: "PC CLIA Source",
  priority: "Case Priority",
  qaReviewHold: "QA Review",
  source: "Source",
  specimenBagBarcode: "Specimen Bag Barcode",
  status: "Status",
  type: "Case Type",
};

export const createMapCaseFilterToOptions: (
  labs: Lab[]
) => FilterOptions<CaseFilterState> = (labs) => ({
  accessioningLab: labs?.map((v) => ({ label: v.name, value: `${v.id}` })),
  billingComponent: [
    {
      label: "TC Only",
      value: "tc_only",
    },
    {
      label: "PC Only",
      value: "pc_only",
    },
    {
      label: "PC and TC",
      value: "global",
    },
  ],
  billerName: ["Telcor", "Optum"].map((b) => ({ label: b, value: b })),
  billingType: Object.values(CaseAdmin.billing_type).map((v) => ({
    label: billingTypeEnumToLabel(v),
    value: v,
  })),
  clinicNumber: ["P-Number", "Other"].map((v) => ({
    label: v,
    value: v,
  })),
  insuranceCategory: [
    { label: "Government", value: "government" },
    { label: "Commercial", value: "commercial" },
  ],
  patientAddressState: locationOptions,
  patientClinicLocationState: locationOptions,
  pcCliaSource: [
    { label: "Internal", value: "internal" },
    { label: "External", value: "external" },
  ],
  priority: Object.values(CaseAdmin.priority).map((v) => ({
    label: displayPriority(v),
    value: v,
  })),
  qaReviewHold: [
    { label: qaReviewLabels.reviewed, value: "reviewed" },
    { label: qaReviewLabels.unreviewed, value: "unreviewed" },
    { label: qaReviewLabels["on-hold"], value: "on-hold" },
  ],
  source: Object.values(CaseAdmin.source)
    .filter((value) => value !== CaseAdmin.source.APL)
    .map((v) => ({
      label: v,
      value: v,
    })),
  status: Object.values(CaseAdmin.status).map((v) => ({
    label: v,
    value: v,
  })),
  type: [
    CaseAdmin.type.CLINICAL,
    CaseAdmin.type.CONSULT,
    CaseAdmin.type.EDUCATION,
    CaseAdmin.type.HISTORICAL,
    CaseAdmin.type.MOHS_QC,
    CaseAdmin.type.TC_PC,
    CaseAdmin.type.TEST,
    CaseAdmin.type.TRIAGE,
  ].map((v) => ({
    label: v,
    value: v,
  })),
});
const CaseFilters = (props: CaseFiltersProps) => {
  const mapCaseFilterToOptions = createMapCaseFilterToOptions(props.labs);
  const adminWPLFilters = useAppSelector((state) =>
    selectFeatureFlag(state, "adminWPLFilters")
  );
  const insuranceFilters = useAppSelector((state) =>
    selectFeatureFlag(state, "insuranceFilters")
  );
  const cliaSourceFilter = useAppSelector((state) =>
    selectFeatureFlag(state, "cliaSourceFilter")
  );

  // When billing type is set to "No bill", we need to disable billingComponent
  useEffect(() => {
    if (
      props.billingType === CaseAdmin.billing_type.NONE &&
      props.billingComponent !== ""
    ) {
      props.onChange("billingComponent", "");
    }
  }, [props.billingType]);

  const billingComponentDisabled =
    props.billingType === CaseAdmin.billing_type.NONE;

  return (
    <>
      <TextInput
        id="accession"
        value={props.accession}
        label={mapCaseFilterToLabel["accession"]}
        onChange={(e: any) => props.onChange("accession", e.target.value)}
        onClear={() => props.onChange("accession", "")}
      />
      <SelectInput
        id="isManuallyAccessioned"
        label={mapQueryFilterToLabel["isManuallyAccessioned"]}
        defaultLabel="All Methods..."
        options={mapQueryFilterToOptions["isManuallyAccessioned"]}
        value={
          props.isManuallyAccessioned === undefined
            ? ""
            : props.isManuallyAccessioned
            ? "true"
            : "false"
        }
        onChange={(e: ChangeEvent<HTMLSelectElement>) => {
          props.onChangeManuallyAccessioned(
            "isManuallyAccessioned",
            e.target.value === "" ? undefined : e.target.value === "true"
          );
        }}
      />
      <SelectInput
        id="type"
        label={mapCaseFilterToLabel["type"]}
        defaultLabel="All Types..."
        options={mapCaseFilterToOptions["type"]}
        value={props.type}
        onChange={(e: ChangeEvent<HTMLSelectElement>) =>
          props.onChange("type", e.target.value)
        }
      />
      <TextInput
        id="activeAssignmentName"
        value={props.activeAssignmentName}
        label={mapCaseFilterToLabel["activeAssignmentName"]}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          props.onChange("activeAssignmentName", e.target.value)
        }
        onClear={() => props.onChange("activeAssignmentName", "")}
      />
      <TextInput
        id="orderingDoctorName"
        value={props.orderingDoctorName}
        label={mapCaseFilterToLabel["orderingDoctorName"]}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          props.onChange("orderingDoctorName", e.target.value);
        }}
        onClear={() => props.onChange("orderingDoctorName", "")}
      />
      <TextInput
        id="patientName"
        value={props.patientName}
        label={mapCaseFilterToLabel["patientName"]}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          props.onChange("patientName", e.target.value)
        }
        onClear={() => props.onChange("patientName", "")}
      />
      <CustomInput
        label={mapCaseFilterToLabel["patientDateOfBirth"]}
        id="patientDateOfBirth"
        onClear={() => props.onChange("patientDateOfBirth", "")}
      >
        <DateInput
          value={props.patientDateOfBirth}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            props.onChange("patientDateOfBirth", e.target.value)
          }
          showPickerOnFocus
          placeholder=" "
        />
      </CustomInput>
      <SelectInput
        id="patientState"
        label={mapCaseFilterToLabel["patientAddressState"]}
        defaultLabel="All States"
        options={locationOptions}
        value={props.patientAddressState}
        onChange={(e: ChangeEvent<HTMLSelectElement>) =>
          props.onChange("patientAddressState", e.target.value)
        }
      />
      <TextInput
        id="patientInsuranceInsuranceCompanyName"
        value={props.patientInsuranceInsuranceCompanyName}
        label={mapCaseFilterToLabel["patientInsuranceInsuranceCompanyName"]}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          props.onChange("patientInsuranceInsuranceCompanyName", e.target.value)
        }
        onClear={() =>
          props.onChange("patientInsuranceInsuranceCompanyName", "")
        }
      />
      {insuranceFilters && (
        <SelectInput
          id="insuranceCategory"
          label={mapCaseFilterToLabel["insuranceCategory"]}
          defaultLabel="All Categories"
          options={mapCaseFilterToOptions["insuranceCategory"]}
          value={props.insuranceCategory || ""}
          onChange={(e: ChangeEvent<HTMLSelectElement>) =>
            props.onChange("insuranceCategory", e.target.value)
          }
        />
      )}
      <ClinicGroupFilter
        onChange={(newValue: { value: string }) => {
          props.onChange("patientClinicClinicGroupName", newValue.value);
        }}
        value={props.patientClinicClinicGroupName}
        label={mapCaseFilterToLabel["patientClinicClinicGroupName"]}
      />
      <SonicPracticeFilter
        onChange={(newValue: { value: string }) => {
          props.onChange("sonicPracticeName", newValue.value);
          if (props.patientClinicSonicPracticeName)
            props.onChange("patientClinicSonicPracticeName", undefined);
        }}
        label={mapCaseFilterToLabel["sonicPracticeName"]}
        value={props.sonicPracticeName}
      />
      <AdminClinicFilter
        onChange={(newValue: { value: string; object: Clinic }) => {
          props.onChange("patientClinicId", newValue.value);
          props.onChangeFilterObject(newValue.object);
        }}
        onInitialize={props.onInitializeClinic}
        label={mapCaseFilterToLabel["patientClinicId"]}
        value={props.patientClinicId}
        object={props.patientClinic}
      />
      <SelectInput
        id="patientClinicLocationState"
        label={mapCaseFilterToLabel["patientClinicLocationState"]}
        defaultLabel="All States"
        options={locationOptions}
        value={props.patientClinicLocationState}
        onChange={(e: ChangeEvent<HTMLSelectElement>) =>
          props.onChange("patientClinicLocationState", e.target.value)
        }
      />
      <SelectInput
        id="accessioningLab"
        label={mapCaseFilterToLabel["accessioningLab"]}
        defaultLabel="All Labs"
        value={props.accessioningLab}
        options={mapCaseFilterToOptions["accessioningLab"]}
        onChange={(e: any) =>
          props.onChange("accessioningLab", e.target.value || "")
        }
      />
      <SelectInput
        id="status"
        label={mapCaseFilterToLabel["status"]}
        defaultLabel="All Statuses"
        options={mapCaseFilterToOptions["status"]}
        value={props.status}
        onChange={(e: ChangeEvent<HTMLSelectElement>) =>
          props.onChange("status", e.target.value)
        }
      />
      <SelectInput
        id="priority"
        label={mapCaseFilterToLabel["priority"]}
        defaultLabel="All Priorities"
        options={mapCaseFilterToOptions["priority"]}
        value={props.priority || ""}
        onChange={(e: ChangeEvent<HTMLSelectElement>) =>
          props.onChange("priority", e.target.value)
        }
      />
      {adminWPLFilters && (
        <SelectInput
          id="source"
          label={mapCaseFilterToLabel["source"]}
          defaultLabel="All Sources"
          options={mapCaseFilterToOptions["source"]}
          value={props.source}
          onChange={(e: ChangeEvent<HTMLSelectElement>) =>
            props.onChange("source", e.target.value)
          }
        />
      )}

      <CliaCertificateFilter
        label={mapCaseFilterToLabel["grossedByCert"]}
        allLabel="All TC CLIA Certificates"
        onChange={({ value }: { value: string }) => {
          props.onChange("grossedByCert", value);
        }}
        value={props.grossedByCert}
      />
      <CliaCertificateFilter
        label={mapCaseFilterToLabel["pcClia"]}
        allLabel="All PC CLIA Certificates"
        onChange={({ value }: { value: string }) => {
          props.onChange("pcClia", value);
        }}
        value={props.pcClia}
      />
      {cliaSourceFilter && (
        <SelectInput
          id="pcCliaSource"
          label={mapCaseFilterToLabel["pcCliaSource"]}
          defaultLabel="All CLIA Sources"
          options={mapCaseFilterToOptions["pcCliaSource"]}
          value={props.pcCliaSource || ""}
          onChange={(e: ChangeEvent<HTMLSelectElement>) =>
            props.onChange("pcCliaSource", e.target.value)
          }
        />
      )}
      <SelectInput
        id="billingType"
        label={mapCaseFilterToLabel["billingType"]}
        defaultLabel="All Billing Types"
        options={mapCaseFilterToOptions["billingType"]}
        value={props.billingType}
        onChange={(e: ChangeEvent<HTMLSelectElement>) => {
          props.onChange("billingType", e.target.value);
        }}
      />
      <SelectInput
        id="billingComponent"
        label={mapCaseFilterToLabel["billingComponent"]}
        defaultLabel={
          billingComponentDisabled ? "Not applicable" : "Any Billing Component"
        }
        options={mapCaseFilterToOptions["billingComponent"]}
        value={billingComponentDisabled ? "" : props.billingComponent}
        onChange={(e: ChangeEvent<HTMLSelectElement>) => {
          props.onChange("billingComponent", e.target.value);
        }}
        disabled={billingComponentDisabled}
      />
      <MultiSelectColumnInput
        id="qaReviewHold"
        label={mapCaseFilterToLabel["qaReviewHold"]}
        options={mapCaseFilterToOptions["qaReviewHold"]}
        value={props.qaReviewHold}
        onChange={(newValue: any) => props.onChange("qaReviewHold", newValue)}
      />
      <SelectInput
        id="billerName"
        label={mapCaseFilterToLabel["billerName"]}
        defaultLabel=""
        options={mapCaseFilterToOptions["billerName"]}
        value={props.billerName || ""}
        onChange={(e: ChangeEvent<HTMLSelectElement>) =>
          props.onChange("billerName", e.target.value)
        }
      />
      <SelectInput
        id="clinicNumber"
        label={mapCaseFilterToLabel["clinicNumber"]}
        defaultLabel=""
        options={mapCaseFilterToOptions["clinicNumber"]}
        value={props.clinicNumber || ""}
        onChange={(e: ChangeEvent<HTMLSelectElement>) =>
          props.onChange("clinicNumber", e.target.value)
        }
      />
      <TextInput
        id="specimenBagBarcode"
        value={props.specimenBagBarcode}
        label={mapCaseFilterToLabel["specimenBagBarcode"]}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          props.onChange("specimenBagBarcode", e.target.value)
        }
        onClear={() => props.onChange("specimenBagBarcode", "")}
      />
    </>
  );
};

export default CaseFilters;
